// src/components/kit.jsx — small shared building blocks for the Finance / Inventory screens:
// cards, tabs, a data table (sortable, totals row, Excel export), a modal, form fields.
import React, { useMemo, useState } from 'react';
import { exportStyledExcel, MONEY_FMT, TOTAL_FILL } from '../utils/exportExcel';

export const GREEN = '#2e9f86';
export const money = (n) => { const v = Number(n) || 0; return `${v < 0 ? '−' : ''}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
export const qty = (n) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 3 });
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');
export const isoDay = (d = new Date()) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
export const errOf = (e) => e?.response?.data?.error || e?.message || String(e);
export const Spin = () => <div className="p-10 text-center text-gray-400">Loading…</div>;
export const Card = ({ children, className = '' }) => <div className={`bg-white rounded-xl border border-gray-200 ${className}`}>{children}</div>;
export const inputCls = 'border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-emerald-200';

export function Btn({ children, kind = 'primary', className = '', ...p }) {
  const style = kind === 'primary' ? { background: GREEN, color: '#fff' } : {};
  const cls = kind === 'primary' ? 'text-white' : kind === 'danger' ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50';
  return <button {...p} style={{ ...style, ...(p.style || {}) }} className={`px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 ${cls} ${className}`}>{children}</button>;
}

export function Stat({ label, value, tone = 'gray', hint }) {
  const color = { green: 'text-emerald-700', red: 'text-red-600', amber: 'text-amber-600', blue: 'text-blue-700', gray: 'text-gray-800' }[tone];
  return (
    <Card className="p-4">
      <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">{label}</div>
      <div className={`text-xl font-bold mt-1 ${color}`}>{value}</div>
      {hint && <div className="text-xs text-gray-400 mt-1">{hint}</div>}
    </Card>
  );
}

export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="flex gap-1 border-b overflow-x-auto mb-4">
      {tabs.map((tb) => {
        const on = tb.key === active;
        return (
          <button key={tb.key} onClick={() => onChange(tb.key)}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition ${on ? '' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            style={on ? { color: GREEN, borderColor: GREEN } : {}}>{tb.label}</button>
        );
      })}
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-gray-600 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-gray-400 mt-1">{hint}</span>}
    </label>
  );
}

export function Modal({ title, onClose, children, footer, wide }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-xl shadow-xl w-full ${wide ? 'max-w-4xl' : 'max-w-lg'} max-h-[90vh] flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-bold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
        {footer && <div className="px-5 py-3 border-t flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * <DataTable columns={[{ key, label, money?, num?, date?, render?, width? , total? }]} rows=[] excel={{ filename, title }} />
 * money/num/total columns get a TOTAL row; click a header to sort.
 */
export function DataTable({ columns, rows, excel, empty = 'Nothing here yet.', rowClass, onRow, footerNote }) {
  const [sort, setSort] = useState(null);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const c = columns.find((x) => x.key === sort.key);
    const val = (r) => (c && c.sortValue ? c.sortValue(r) : r[sort.key]);
    return [...rows].sort((a, b) => { const x = val(a), y = val(b); return (x > y ? 1 : x < y ? -1 : 0) * sort.dir; });
  }, [rows, sort, columns]);
  const totals = columns.some((c) => c.total) ? Object.fromEntries(columns.filter((c) => c.total).map((c) => [c.key, rows.reduce((s, r) => s + (Number(r[c.key]) || 0), 0)])) : null;
  const cell = (c, r) => {
    if (c.render) return c.render(r);
    const v = r[c.key];
    if (c.money) return money(v);
    if (c.num) return qty(v);
    if (c.date) return fmtDate(v);
    return v ?? '';
  };
  const doExcel = () => exportStyledExcel({
    filename: excel.filename, sheetName: (excel.title || 'Sheet').slice(0, 30), title: excel.title, subtitle: excel.subtitle,
    columns: columns.filter((c) => c.excel !== false).map((c) => ({ header: c.label, key: c.key, width: c.width || (c.money ? 15 : 22), ...(c.money ? { numFmt: MONEY_FMT, align: 'right' } : {}) })),
    rows: [
      ...sorted.map((r) => Object.fromEntries(columns.filter((c) => c.excel !== false).map((c) => [c.key, c.excelValue ? c.excelValue(r) : c.money || c.num ? Number(r[c.key]) || 0 : c.date ? fmtDate(r[c.key]) : (typeof r[c.key] === 'object' ? '' : r[c.key])]))),
      ...(totals ? [{ ...Object.fromEntries(columns.map((c) => [c.key, c.total ? totals[c.key] : ''])), [columns[0].key]: 'TOTAL', __bold: true, __fill: TOTAL_FILL }] : []),
    ],
  });
  return (
    <Card className="overflow-x-auto">
      {(excel || footerNote) && (
        <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
          <span className="text-xs text-gray-500">{rows.length} rows{footerNote ? ` · ${footerNote}` : ''}</span>
          {excel && <button onClick={doExcel} className="text-xs font-semibold text-emerald-700 hover:underline">⬇ Excel</button>}
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b text-xs uppercase text-gray-500">
          <tr>{columns.map((c) => (
            <th key={c.key} onClick={() => setSort((s) => ({ key: c.key, dir: s && s.key === c.key ? -s.dir : 1 }))}
              className={`px-3 py-2 cursor-pointer select-none whitespace-nowrap ${c.money || c.num ? 'text-right' : 'text-left'}`}>
              {c.label}{sort && sort.key === c.key ? (sort.dir > 0 ? ' ▲' : ' ▼') : ''}
            </th>))}</tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {!sorted.length && <tr><td colSpan={columns.length} className="text-center text-gray-400 py-10">{empty}</td></tr>}
          {sorted.map((r, i) => (
            <tr key={r._id || r.key || i} className={`${onRow ? 'cursor-pointer hover:bg-gray-50' : ''} ${rowClass ? rowClass(r) : ''}`} onClick={onRow ? () => onRow(r) : undefined}>
              {columns.map((c) => <td key={c.key} className={`px-3 py-1.5 ${c.money || c.num ? 'text-right tabular-nums' : ''} ${c.className || ''}`}>{cell(c, r)}</td>)}
            </tr>
          ))}
          {totals && sorted.length > 0 && (
            <tr className="bg-emerald-50 font-bold">
              {columns.map((c, i) => <td key={c.key} className={`px-3 py-2 ${c.money || c.num ? 'text-right tabular-nums' : ''}`}>{i === 0 ? 'TOTAL' : c.total ? (c.money ? money(totals[c.key]) : qty(totals[c.key])) : ''}</td>)}
            </tr>
          )}
        </tbody>
      </table>
    </Card>
  );
}

/** a <select> of the shop's payment methods (cash / bank / OMT / Whish / cheque…) */
export function MethodSelect({ methods = [], value, onChange, allowEmpty, emptyLabel = '— choose —', filter }) {
  const list = methods.filter((m) => m.status !== 'inactive' && (!filter || filter(m)));
  return (
    <select className={inputCls} value={value || ''} onChange={(e) => onChange(e.target.value)}>
      {allowEmpty && <option value="">{emptyLabel}</option>}
      {!allowEmpty && !value && <option value="">{emptyLabel}</option>}
      {list.map((m) => <option key={m._id} value={m._id}>{m.name}{m.type ? ` (${m.type.replace('_', ' ')})` : ''}</option>)}
    </select>
  );
}
